import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getUserProjects } from 'services/project.service';
import { FolderType } from 'services/folders.service';
import { ObjectType } from 'services/access.service';
import { createSafeAsyncThunk } from 'store/store.helper';
import { UNFILED_FOLDER_ID, PROJECTS_HEADERS_SELECTS, ALL_ACTIVE_FOLDER_ID } from './projects.params';

const SLICE_ID = 'projects';

export const PROJECT_TABLES_SEARCH = [
  'foldersSearchFilter',
  'filesSearchFilter',
  'projectsSearchFilter',
  'storiesSearchFilter',
  'finalCutsSearchFilter',
] as const;
export type ProjectTableSearchKey = (typeof PROJECT_TABLES_SEARCH)[number];

export const PROJECT_TABLES_STATE = [
  'foldersTableState',
  'filesTableState',
  'projectsTableState',
  'storiesTableState',
  'finalCutsTableState',
] as const;
export type ProjectTableStateKey = (typeof PROJECT_TABLES_STATE)[number];

export const PROJECT_TABLES_SORT = [
  'foldersTableSort',
  'filesTableSort',
  'projectsTableSort',
  'storiesTableSort',
  'finalCutsTableSort',
] as const;
export type ProjectTableSortKey = (typeof PROJECT_TABLES_SORT)[number];

export type Sort = { column: number; order: 'ASC' | 'DESC' };

interface State {
  filesTableSort: Sort;
  filesSearchFilter: string | null;
  filesTableState: boolean;

  foldersTableSort: Sort;
  foldersTableState: boolean;
  foldersSearchFilter: string | null;

  projectsTableState: boolean;
  projectsTableSort: Sort;
  projectsSearchFilter: string | null;

  storiesTableSort: Sort;
  storiesTableState: boolean;
  storiesSearchFilter: string | null;

  finalCutsTableSort: Sort;
  finalCutsTableState: boolean;
  finalCutsSearchFilter: string | null;

  selectedProject: ProjectId | null;
  selectedFolder: FolderId | typeof UNFILED_FOLDER_ID | null;
  selectedFile: FileId | null;
  selectedStory: StoryId | null;
  selectedFinalcut: FinalcutId | null;

  hoveredFile: FileId | null;
  hoveredFc: FinalcutId | null;

  foldersAvailable: boolean;
  accessTypeModal: ObjectType | null;
  dateEditor: FileId | null;
}

const initialState: State = {
  foldersTableState: false,
  foldersSearchFilter: null,
  foldersTableSort: { column: PROJECTS_HEADERS_SELECTS.folders, order: 'ASC' },

  filesTableState: true,
  filesSearchFilter: null,
  filesTableSort: { column: PROJECTS_HEADERS_SELECTS.files, order: 'ASC' },

  projectsTableState: true,
  projectsSearchFilter: null,
  projectsTableSort: { column: PROJECTS_HEADERS_SELECTS.projects, order: 'ASC' },

  storiesTableState: true,
  storiesSearchFilter: null,
  storiesTableSort: { column: PROJECTS_HEADERS_SELECTS.stories, order: 'ASC' },

  finalCutsTableState: true,
  finalCutsSearchFilter: null,
  finalCutsTableSort: { column: PROJECTS_HEADERS_SELECTS.finalcuts, order: 'ASC' },

  selectedProject: null,
  selectedFolder: null,
  selectedFile: null,
  selectedStory: null,
  selectedFinalcut: null,

  hoveredFile: null,
  hoveredFc: null,

  foldersAvailable: false,
  accessTypeModal: null,
  dateEditor: null,
};

export const checkFoldersAvailable = createSafeAsyncThunk(
  SLICE_ID + '/checkFoldersAvailable',
  async (_, { getState }) => {
    const { folders, projects } = getState().app__data;
    const { selectedFolder } = getState().projects;
    if (!folders || !projects || !selectedFolder) return;
    if (selectedFolder !== UNFILED_FOLDER_ID) return true;
    if ([UNFILED_FOLDER_ID, ALL_ACTIVE_FOLDER_ID].includes(selectedFolder) && projects.length >= 5) return true;
    if (folders.some((el) => el.type === FolderType.Custom)) return true;
    for (const folder of folders.filter((el) => el.type !== FolderType.Custom)) {
      if (selectedFolder === folder.id && projects.length) return true;
      const res = (await getUserProjects({ folderId: folder.id })).data;
      if (res.length) return true;
    }
  }
);

// S L I C E

export const slice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    setTableSearchFilter: (
      state,
      action: PayloadAction<{
        key: ProjectTableSearchKey;
        value: string | null;
      }>
    ) => {
      state[action.payload.key] = action.payload.value;
    },
    setTableState: (state, action: PayloadAction<{ key: ProjectTableStateKey; value: boolean }>) => {
      state[action.payload.key] = action.payload.value;
    },
    setTableSort: (state, action: PayloadAction<{ key: ProjectTableSortKey; value: Sort }>) => {
      state[action.payload.key] = action.payload.value ?? initialState[action.payload.key];
    },
    setSelectedFolder: (state, action: PayloadAction<FolderId>) => {
      state.selectedFolder = action.payload;
    },
    setSelectedProject: (state, action: PayloadAction<ProjectId | null>) => {
      state.selectedProject = action.payload;
    },
    setSelectedFile: (state, action: PayloadAction<FileId | null>) => {
      state.selectedFile = action.payload;
    },
    setSelectedStory: (state, action: PayloadAction<StoryId | null>) => {
      state.selectedStory = action.payload;
    },
    setSelectedFinalcut: (state, action: PayloadAction<FinalcutId | null>) => {
      state.selectedFinalcut = action.payload;
    },
    setHoveredFile: (state, action: PayloadAction<FileId | null>) => {
      state.hoveredFile = action.payload;
    },
    setHoveredFc: (state, action: PayloadAction<FinalcutId | null>) => {
      state.hoveredFc = action.payload;
    },
    setAccessTypeModal: (state, action: PayloadAction<State['accessTypeModal']>) => {
      console.trace('[DEBUG] setAccessTypeModal called', {
        newValue: action.payload,
        previousValue: state.accessTypeModal,
        selectedProject: state.selectedProject,
        selectedStory: state.selectedStory,
        selectedFinalcut: state.selectedFinalcut,
        selectedFile: state.selectedFile,
        filesTableState: state.filesTableState,
        storiesTableState: state.storiesTableState,
        projectsTableState: state.projectsTableState,
        finalCutsTableState: state.finalCutsTableState,
      });
      state.accessTypeModal = action.payload;
    },
    setDateEditor: (state, action: PayloadAction<State['dateEditor']>) => {
      state.dateEditor = action.payload;
    },
    setFoldersAvailable: (state, action: PayloadAction<boolean>) => {
      state.foldersAvailable = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(checkFoldersAvailable.fulfilled, (state, action) => {
      state.foldersAvailable = !!action.payload;
    });
  },
});

export const {
  setTableSearchFilter,
  setSelectedFinalcut,
  setSelectedProject,
  setSelectedFolder,
  setSelectedStory,
  setSelectedFile,
  setTableState,
  setTableSort,
  setHoveredFc,
  setDateEditor,
  setHoveredFile,
  setAccessTypeModal,
  setFoldersAvailable,
} = slice.actions;
export default slice.reducer;
